// Inicialización de i18next para la PWA (i18n multi-idioma).
//
// Carga los 8 catálogos de idioma soportados (inglés y español revisados; los
// demás son traducciones automáticas pendientes de revisión por un hablante
// nativo). El idioma PRINCIPAL y de respaldo para claves faltantes es el inglés.
// El idioma INICIAL se resuelve por precedencia:
//   1. idioma recordado en localStorage (elección previa del usuario sin sesión),
//   2. idioma del navegador (si está entre los soportados),
//   3. inglés (respaldo).
// Tras iniciar sesión, el idioma GUARDADO del usuario en el backend PREVALECE
// (lo aplica LanguageProvider al recibir el perfil).

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import es from './locales/es.json';
import en from './locales/en.json';
import zh from './locales/zh.json';
import hi from './locales/hi.json';
import pt from './locales/pt.json';
import fr from './locales/fr.json';
import ru from './locales/ru.json';
import bn from './locales/bn.json';

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_CODE_LIST,
  detectBrowserLocale,
  isSupportedLocale,
  localeInfo,
  type Locale,
} from './locales.js';

/** Recursos de traducción por idioma (namespace único por ahora: default). */
const resources = {
  es: { translation: es },
  en: { translation: en },
  zh: { translation: zh },
  hi: { translation: hi },
  pt: { translation: pt },
  fr: { translation: fr },
  ru: { translation: ru },
  bn: { translation: bn },
} as const;

/** Lee el idioma recordado en localStorage (o null si no hay uno válido). */
function readStoredLocale(): Locale | null {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Idioma inicial por precedencia (localStorage → navegador → respaldo). */
function initialLocale(): Locale {
  return readStoredLocale() ?? detectBrowserLocale(DEFAULT_LOCALE);
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_CODE_LIST,
  interpolation: { escapeValue: false }, // React ya escapa.
  returnNull: false,
});

/** Fija el atributo `lang` del documento (accesibilidad / SEO). */
function syncDocumentLang(locale: Locale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = localeInfo(locale).tag;
  }
}
syncDocumentLang(i18n.language as Locale);

/**
 * Cambia el idioma activo y lo recuerda en localStorage. `persistLocal` permite
 * NO escribir en localStorage (p. ej. cuando el idioma proviene del perfil del
 * usuario, que ya es la fuente de verdad). Devuelve una promesa que resuelve
 * cuando i18next ha cambiado.
 */
export async function changeLocale(locale: Locale, persistLocal = true): Promise<void> {
  if (!isSupportedLocale(locale)) return;
  await i18n.changeLanguage(locale);
  syncDocumentLang(locale);
  if (persistLocal) {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // Ignorado: el idioma se mantiene en memoria aunque no se pueda persistir.
    }
  }
}

/** Idioma activo actual (normalizado a un código soportado). */
export function currentLocale(): Locale {
  return isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}

export { i18n };
export * from './locales.js';
